import type { MessagePart } from './message-part-utils';

import {
  getToolTraceName,
  humanizeToolName,
  isDynamicToolPart,
  isGenericStaticToolPart,
  isInteractiveProposalToolPart,
  isStepStartPart,
} from './message-part-utils';

// ── Trace-group segmentation ──────────────────────────────────────────
// Identifies contiguous runs of tool/reasoning/step-start parts that precede
// a text part and where every tool has reached a terminal state. These runs
// are collapsed into a single ToolTraceGroup toggle in the rendered output.

type TraceSegment = { kind: 'trace-group'; startIndex: number; endIndex: number };
type SingleSegment = { kind: 'single'; index: number };
export type PartSegment = TraceSegment | SingleSegment;

// Interactive proposal cards contain visible Apply/Cancel decisions. Keep them
// out of the auto-collapsed trace group so users do not miss the action.
const isTracePart = (part: MessagePart): boolean =>
  !isInteractiveProposalToolPart(part) &&
  (part.type === 'reasoning' ||
    isStepStartPart(part) ||
    part.type.startsWith('tool-') ||
    isDynamicToolPart(part));

const isToolComplete = (part: MessagePart): boolean => {
  if (isDynamicToolPart(part)) {
    return part.state === 'output-available' || part.state === 'output-error';
  }

  if (isGenericStaticToolPart(part)) {
    const state = part.state as string | undefined;
    return state === 'output-available' || state === 'output-error';
  }

  return true;
};

export function segmentParts(parts: MessagePart[]): PartSegment[] {
  const segments: PartSegment[] = [];
  let runStart: number | null = null;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;

    if (isTracePart(part)) {
      if (runStart === null) {
        runStart = i;
      }
      continue;
    }

    // Non-trace part: close any open run
    if (runStart !== null) {
      const runEnd = i - 1;
      const hasTextAfter = part.type === 'text' && part.text.trim().length > 0;

      // Only group if followed by text and every tool in the run is complete
      const allComplete =
        hasTextAfter &&
        parts
          .slice(runStart, runEnd + 1)
          .every(
            (p) => (!p.type.startsWith('tool-') && !isDynamicToolPart(p)) || isToolComplete(p),
          );

      const hasMultipleTraces = runEnd > runStart;
      const hasAtLeastOneTool = parts
        .slice(runStart, runEnd + 1)
        .some((p) => p.type.startsWith('tool-') || isDynamicToolPart(p));

      if (hasTextAfter && allComplete && hasMultipleTraces && hasAtLeastOneTool) {
        segments.push({ kind: 'trace-group', startIndex: runStart, endIndex: runEnd });
      } else {
        for (let j = runStart; j <= runEnd; j++) {
          segments.push({ kind: 'single', index: j });
        }
      }

      runStart = null;
    }

    segments.push({ kind: 'single', index: i });
  }

  // Trailing run with no text after — render individually
  if (runStart !== null) {
    for (let j = runStart; j < parts.length; j++) {
      segments.push({ kind: 'single', index: j });
    }
  }

  return segments;
}

export function buildTraceSummary(parts: MessagePart[], startIndex: number, endIndex: number) {
  const toolNames: string[] = [];
  let toolCount = 0;

  for (let i = startIndex; i <= endIndex; i++) {
    const part = parts[i]!;

    if (isDynamicToolPart(part)) {
      toolCount++;
      toolNames.push(humanizeToolName(part.toolName));
    } else if (part.type.startsWith('tool-')) {
      toolCount++;
      toolNames.push(getToolTraceName(part.type));
    }
  }

  return { toolCount, toolNames };
}
